// FakturowniaPaymentResolutionService — operator resolutions over Fakturownia
// payment allocations (the reconcile-queue actions: reject / accept /
// link-to-bank-line / split).
//
// Every resolution is a STICKY operator decision pinned to the payment's LIVE
// checksum (design §Principles): the service reads the current payment, stamps
// `resolution_checksum = payment.checksum`, and the reconciler honors it only
// while that checksum still matches — a later FA-side edit auto-invalidates it
// and the row re-enters the queue. The accept path re-uses the SAME outstanding
// math as PaymentReconciler (family-root-anchored effective gross vs. bank links
// + persisted fa_only evidence) so an over-payment is flagged, not silently
// double-counted.
//
// This class writes ONLY local read-model state (allocation resolutions via the
// DAO, and — when it links a bank line — that link's push status). It NEVER
// writes to `invoices` (the DB recompute trigger does that off the allocation
// state changes) and NEVER calls Fakturownia.
//
// Deno-compatible: explicit .ts extensions, no Node-only APIs.

import type { FakturowniaPaymentDao } from './FakturowniaPaymentDao.ts';
import type { MbankPaymentLinkDao } from '../../mbank/dao/MbankPaymentLinkDao.ts';
import type { MbankStatementLineDao } from '../../mbank/dao/MbankStatementLineDao.ts';
import type { CompanyBankAccountDao } from '../../mbank/dao/CompanyBankAccountDao.ts';
import type { InvoiceDao } from '../../invoice/InvoiceDao.ts';
import { isIbanShaped } from '../../mbank/matching/ibanShape.ts';
import { effectiveGrossMagnitude } from './PaymentReconciler.ts';
import type {
  FakturowniaPaymentAllocationRow,
  FakturowniaPaymentAllocationState,
  FakturowniaPaymentRow,
} from '@shared/types/fakturownia_payment.ts';

/** Amount tolerance (ε) — mirrors PaymentReconciler + the bank-link math. */
const EPS = 0.01;

/** Result of a single-allocation resolution. `split` returns one per allocation. */
export interface AllocationResolutionResult {
  allocationId: number;
  state: FakturowniaPaymentAllocationState;
  invoiceId: number | null;
}

/** One entry in a `split` request: the target allocation + its share of the payment. */
export interface AllocationSplit {
  allocationId: number;
  amount: number;
}

export class FakturowniaPaymentResolutionService {
  constructor(
    private readonly dao: FakturowniaPaymentDao,
    private readonly linkDao: MbankPaymentLinkDao,
    private readonly invoiceDao: InvoiceDao,
    // Only needed by the interim payment-level pairing action `pairToLine`
    // (design doc 2026-07-28). Absent for every other caller/action.
    private readonly lineDao?: MbankStatementLineDao,
    // Optional trust-fold sink (T7 remediation 2026-08-03): when present,
    // a successful debit-line pairing also observes/promotes the seller's
    // account and stamps the line's counterparty company. Best-effort —
    // absence or failure never affects the pairing itself.
    private readonly companyBankDao?: CompanyBankAccountDao,
  ) {}

  /**
   * Reject a bogus FA allocation — sticky, checksum-pinned. Counts toward
   * nothing until the payment content changes and the reconciler re-derives.
   */
  async reject(allocationId: number, user: string): Promise<AllocationResolutionResult> {
    const { allocation, payment } = await this.loadContext(allocationId);
    await this.dao.setAllocationResolution(allocation.id, {
      resolution: 'rejected',
      resolved_by: user,
      resolution_checksum: payment.checksum,
      state: 'rejected',
    });
    return { allocationId: allocation.id, state: 'rejected', invoiceId: allocation.invoice_id };
  }

  /**
   * Accept an allocation as fa_only evidence (design D1 — it then counts toward
   * the invoice's payment_status). Requires `allocated_amount` set (the caller
   * splits a multi-invoice payment first). Validates against effective
   * outstanding with the SAME family-root math the reconciler uses; acceptance
   * is allowed even when it over-pays (operator override) — the over case is
   * recorded as `match_detail = { overpayment: true }`.
   */
  async accept(allocationId: number, user: string): Promise<AllocationResolutionResult> {
    const { allocation, payment } = await this.loadContext(allocationId);
    if (allocation.allocated_amount == null) {
      throw new Error(
        `Allocation ${allocation.id} has no allocated_amount — split the payment across its invoices before accepting`,
      );
    }
    const overpayment = await this.isOverpayment(allocation, payment);
    await this.dao.setAllocationResolution(allocation.id, {
      resolution: 'accepted',
      resolved_by: user,
      resolution_checksum: payment.checksum,
      state: 'fa_only',
      allocated_amount: allocation.allocated_amount,
      // Only the overpayment flag is written; a clean accept leaves the
      // reconciler's diagnostic match_detail untouched (the DAO writes only
      // the keys it is passed).
      ...(overpayment ? { match_detail: { overpayment: true } } : {}),
    });
    return { allocationId: allocation.id, state: 'fa_only', invoiceId: allocation.invoice_id };
  }

  /**
   * Manually link an allocation to an existing, un-adopted bank line (the FA
   * payment IS our bank-evidenced payment). The link must belong to the
   * allocation's invoice FAMILY and be un-adopted (`fakturownia_payment_id IS
   * NULL`, status ∉ pushed/pushing) — enforced by findUnadoptedByInvoices. On
   * success the link is adopted (marked `pushed` with this payment id) so the
   * two ledgers stay single-counted.
   */
  async linkToBankLine(
    allocationId: number,
    linkId: number,
    user: string,
  ): Promise<AllocationResolutionResult> {
    const { allocation, payment } = await this.loadContext(allocationId);
    if (allocation.invoice_id == null) {
      throw new Error(
        `Allocation ${allocation.id} has no resolved invoice to match a bank line against`,
      );
    }
    const familyIds = await this.invoiceFamily(allocation.invoice_id);
    const candidates = await this.linkDao.findUnadoptedByInvoices(familyIds);
    const link = candidates.find((l) => l.id === linkId);
    if (link == null) {
      throw new Error(
        `Bank link ${linkId} is not an un-adopted link on allocation ${allocation.id}'s invoice family`,
      );
    }
    const now = new Date().toISOString();
    await this.linkDao.setPushStatus(link.id, 'pushed', {
      fakturownia_payment_id: payment.external_payment_id,
      fakturownia_pushed_at: now,
    });
    await this.dao.setAllocationResolution(allocation.id, {
      resolution: 'manual_link',
      resolved_by: user,
      resolution_checksum: payment.checksum,
      state: 'linked',
      mbank_payment_link_id: link.id,
    });
    return { allocationId: allocation.id, state: 'linked', invoiceId: allocation.invoice_id };
  }

  /**
   * Split a multi-invoice payment across its allocations (operator resolves an
   * `unsplittable_multi_invoice` conflict). The splits must cover EXACTLY the
   * payment's allocation id set and sum to |payment.amount| ±0.01; each
   * allocation is then accepted as fa_only with its share stored as
   * `allocated_amount` — sticky, checksum-pinned.
   */
  async split(
    externalPaymentId: string,
    splits: AllocationSplit[],
    user: string,
  ): Promise<AllocationResolutionResult[]> {
    const payment = await this.dao.getPayment(externalPaymentId);
    if (payment == null) throw new Error(`Payment ${externalPaymentId} not found`);
    if (payment.remote_deleted_at != null) {
      throw new Error(`Payment ${externalPaymentId} was deleted in Fakturownia; cannot resolve`);
    }

    const allocations = await this.dao.listAllocations(externalPaymentId);
    const allocIds = new Set(allocations.map((a) => a.id));
    const splitIds = new Set(splits.map((s) => s.allocationId));
    if (splitIds.size !== splits.length) {
      throw new Error('split contains duplicate allocation ids');
    }
    // Exact cover: equal size + every allocation present ⇒ the sets are identical.
    if (splitIds.size !== allocIds.size || [...allocIds].some((id) => !splitIds.has(id))) {
      throw new Error(
        `splits must cover exactly the payment's allocations (expected ids [${[...allocIds].join(',')}])`,
      );
    }

    const sum = splits.reduce((acc, s) => acc + s.amount, 0);
    if (Math.abs(sum - Math.abs(payment.amount)) > EPS) {
      throw new Error(
        `split amounts (${sum}) must sum to the payment amount (${Math.abs(payment.amount)}) within ±${EPS}`,
      );
    }

    const byId = new Map(allocations.map((a) => [a.id, a]));
    const results: AllocationResolutionResult[] = [];
    for (const s of splits) {
      const alloc = byId.get(s.allocationId) as FakturowniaPaymentAllocationRow;
      await this.dao.setAllocationResolution(s.allocationId, {
        resolution: 'accepted',
        resolved_by: user,
        resolution_checksum: payment.checksum,
        state: 'fa_only',
        allocated_amount: s.amount,
      });
      results.push({ allocationId: s.allocationId, state: 'fa_only', invoiceId: alloc.invoice_id });
    }
    return results;
  }

  // ── Interim payment-level pairing resolutions (design doc 2026-07-28) ──
  //
  // Unlike the allocation-level actions above, these two operate directly on
  // `fakturownia_payment`'s `pairing_*` columns (interim payments_invoice_
  // linking=false mode). Same checksum-pinning discipline: the write is
  // pinned to the LIVE payment checksum, so the reconciler's pairBatched
  // sweep honors it only while that checksum still matches and clears +
  // recomputes it the moment FA-side content changes.

  /**
   * Interim: operator accepts a `no_line` (or otherwise unresolved) payment as
   * a legitimate manual entry — no bank statement line will ever explain it
   * (e.g. a cash deposit, a manual FA correction). Sticky until the payment's
   * checksum changes.
   */
  async acceptManualException(externalPaymentId: string, user: string): Promise<void> {
    const payment = await this.loadLivePayment(externalPaymentId);
    await this.dao.setPairingResolution(externalPaymentId, {
      pairing_resolution: 'accepted_manual',
      pairing_resolved_by: user,
      pairing_resolved_at: new Date().toISOString(),
      pairing_resolution_checksum: payment.checksum,
      pairing_status: 'manual_exception',
      paired_statement_line_id: null,
      pairing_basis: null,
    });
  }

  /**
   * Interim: operator pairs a payment (typically `ambiguous` or `no_line`) to
   * a specific mBank statement line they picked by hand. Requires the service
   * to have been constructed with a `lineDao` (absent → throws — the caller
   * asked for a check this instance cannot perform). Rejects when another
   * LIVE payment already claims that line (a bank line pairs to at most one
   * payment) — the duplicate guard the interim ledger's `is_duplicate`
   * indicator would otherwise only catch after the fact.
   */
  async pairToLine(externalPaymentId: string, statementLineId: number, user: string): Promise<void> {
    if (this.lineDao == null) {
      throw new Error('pairToLine requires the service to be constructed with a statement-line DAO');
    }
    const payment = await this.loadLivePayment(externalPaymentId);
    const line = await this.lineDao.getById(statementLineId);

    const existing = await this.dao.findLivePaymentsPairedToLine(statementLineId);
    const duplicate = existing.find((p) => p.external_payment_id !== externalPaymentId);
    if (duplicate != null) {
      throw new Error(
        `Statement line ${statementLineId} is already paired to payment ${duplicate.external_payment_id}`,
      );
    }

    await this.dao.setPairingResolution(externalPaymentId, {
      pairing_resolution: 'manual_pair',
      pairing_resolved_by: user,
      pairing_resolved_at: new Date().toISOString(),
      pairing_resolution_checksum: payment.checksum,
      pairing_status: 'paired',
      paired_statement_line_id: statementLineId,
      pairing_basis: 'resolution',
    });

    await this.foldPairingTrust(externalPaymentId, line);
  }

  /**
   * Best-effort trust fold after a successful manual pairing (T7 remediation
   * 2026-08-03): a paired DEBIT line is payment evidence for the payment's
   * seller company — observe + promote the account and stamp the line's
   * counterparty. Gated exactly like the manual-match learning path: never
   * on reversals, never for non-IBAN-shaped counterparty ids, and only when
   * the payment's allocations resolve to exactly ONE seller company. Every
   * step is wrapped — a fold failure must never fail the pairing.
   */
  private async foldPairingTrust(
    externalPaymentId: string,
    line: {
      id: number;
      direction: string;
      is_reversal: boolean;
      counterparty_iban: string | null;
      counterparty_bic?: string | null;
      currency: string;
    },
  ): Promise<void> {
    if (this.companyBankDao == null || this.lineDao == null) return;
    if (line.direction !== 'debit' || line.is_reversal) return;
    if (!isIbanShaped(line.counterparty_iban)) return;
    try {
      const allocations = await this.dao.listAllocations(externalPaymentId);
      const sellerIds = new Set<number>();
      for (const alloc of allocations) {
        if (alloc.invoice_id == null) continue;
        const inv = (await this.invoiceDao.getById(alloc.invoice_id)) as {
          seller_company_id?: number | null;
        };
        if (inv.seller_company_id != null) sellerIds.add(inv.seller_company_id);
      }
      if (sellerIds.size !== 1) return;
      const [company] = [...sellerIds];
      const iban = line.counterparty_iban as string;
      await this.companyBankDao.observe(
        company,
        iban,
        line.currency,
        { swiftCode: line.counterparty_bic ?? null },
        'observed_match',
      );
      await this.companyBankDao.promoteToPaymentVerified(company, iban);
      await this.lineDao.setCounterpartyCompany(line.id, company);
    } catch {
      // Best-effort by contract — the pairing itself already succeeded.
    }
  }

  /**
   * Interim: operator un-pins a pairing — clears a `manual_pair` link, an
   * `accepted_manual` exception, or an automatic pairing. Every pairing
   * column AND the sweep gate (`pairing_checksum`) are nulled, so the next
   * `pairBatched` pass reclassifies the payment from scratch. Note: an
   * automatically-derived pairing whose facts still hold (unique amount+date
   * line) will re-derive on that sweep — unpair permanently sticks only for
   * operator resolutions; for a wrong automatic pair the fix is in FA or an
   * accepted-manual exception.
   */
  async unpair(externalPaymentId: string): Promise<void> {
    await this.loadLivePayment(externalPaymentId);
    await this.dao.setPairingResolution(externalPaymentId, {
      pairing_resolution: null,
      pairing_resolved_by: null,
      pairing_resolved_at: null,
      pairing_resolution_checksum: null,
      pairing_status: null,
      paired_statement_line_id: null,
      pairing_basis: null,
      pairing_checksum: null,
    });
  }

  // ── internals ───────────────────────────────────────────────────────────

  /**
   * Common guards for the single-allocation actions: the allocation exists, its
   * payment exists, and the payment is not remotely deleted (a deleted FA
   * payment must never be resolved — matrix #8).
   */
  private async loadContext(allocationId: number): Promise<{
    allocation: FakturowniaPaymentAllocationRow;
    payment: FakturowniaPaymentRow;
  }> {
    const allocation = await this.dao.findAllocationById(allocationId);
    if (allocation == null) throw new Error(`Allocation ${allocationId} not found`);
    const payment = await this.dao.getPayment(allocation.external_payment_id);
    if (payment == null) {
      throw new Error(`Payment ${allocation.external_payment_id} not found`);
    }
    if (payment.remote_deleted_at != null) {
      throw new Error(
        `Payment ${allocation.external_payment_id} was deleted in Fakturownia; cannot resolve`,
      );
    }
    return { allocation, payment };
  }

  /**
   * Common guard for the payment-level pairing actions: the payment exists
   * and is not remotely deleted. Mirrors {@link loadContext}'s deleted-payment
   * guard, but keyed directly by external_payment_id (there's no allocation
   * in the payment-level flow).
   */
  private async loadLivePayment(externalPaymentId: string): Promise<FakturowniaPaymentRow> {
    const payment = await this.dao.getPayment(externalPaymentId);
    if (payment == null) throw new Error(`Payment ${externalPaymentId} not found`);
    if (payment.remote_deleted_at != null) {
      throw new Error(`Payment ${externalPaymentId} was deleted in Fakturownia; cannot resolve`);
    }
    return payment;
  }

  /** The invoice's family: its root (COALESCE(correction_of, id)) + all corrections. */
  private async invoiceFamily(invoiceId: number): Promise<number[]> {
    const inv = await this.invoiceDao.getById(invoiceId);
    const parent = (inv as { correction_of_invoice_id?: number | null }).correction_of_invoice_id;
    const root = parent ?? invoiceId;
    const { corrIdToParent } = await this.invoiceDao.getGrossWithCorrectionDeltas([root]);
    return [...new Set([root, ...corrIdToParent.keys()])];
  }

  /**
   * True when accepting this allocation would push the invoice family's settled
   * total past its effective gross (bank links + OTHER payments' persisted
   * fa_only + this allocation), all family-root-anchored — the same over-payment
   * guard PaymentReconciler applies, so accept can flag (not silence) an
   * over-payment.
   */
  private async isOverpayment(
    allocation: FakturowniaPaymentAllocationRow,
    payment: FakturowniaPaymentRow,
  ): Promise<boolean> {
    const invoiceId = allocation.invoice_id;
    if (invoiceId == null) return false;

    const inv = await this.invoiceDao.getById(invoiceId);
    const parent = (inv as { correction_of_invoice_id?: number | null }).correction_of_invoice_id;
    const root = parent ?? invoiceId;

    const { grossById, corrDeltaByParent, corrIdToParent } =
      await this.invoiceDao.getGrossWithCorrectionDeltas([root]);
    const familyIds = [...new Set([root, ...corrIdToParent.keys()])];

    // Bank links across the family, corrections folded onto the root.
    const appliedByOwn = await this.linkDao.sumAppliedByInvoice(familyIds);
    let applied = 0;
    for (const [own, amt] of appliedByOwn) {
      if ((corrIdToParent.get(own) ?? own) === root) applied += amt;
    }

    // Persisted fa_only evidence from OTHER payments (this payment excluded — its
    // own stale allocations must not double-count against the amount we're accepting).
    const faOnlyByOwn = await this.dao.sumFaOnlyByInvoices(familyIds, payment.external_payment_id);
    let persistedFaOnly = 0;
    for (const [own, amt] of faOnlyByOwn) {
      if ((corrIdToParent.get(own) ?? own) === root) persistedFaOnly += amt;
    }

    const effGross = effectiveGrossMagnitude(
      grossById.get(root) ?? 0,
      corrDeltaByParent.get(root) ?? 0,
    );
    const amt = Math.abs(allocation.allocated_amount ?? 0);
    const familySettled = Math.max(0, applied) + Math.max(0, persistedFaOnly) + amt;
    return familySettled > effGross + EPS;
  }
}
